<template>
  <div v-if="isLoading">
    <v-skeleton-loader
      type="table-heading"
      class="mb-4"
    />
    <v-skeleton-loader
      type="card"
      class="mb-8"
    />
    <v-skeleton-loader
      type="card"
      class="mb-8"
    />
    <v-skeleton-loader
      type="card"
      class="mb-8"
    />
  </div>

  <div
    v-else
    ref="top"
  >
    <div class="pa-0 px-1 w-full d-flex align-center justify-end">
      <v-btn
        v-if="!addMode"
        :ripple="false"
        fab
        small
        color="primary"
        @click="addHallMock"
      >
        <v-icon>
          {{ icons.mdiPlus }}
        </v-icon>
      </v-btn>
    </div>

    <v-tabs
      v-model="currentHallIdx"
      show-arrows
      background-color="transparent"
      class="mb-4 elevation-0"
    >
      <v-tab
        v-for="(hall, hallTabIdx) in halls"
        :key="`hall_tab_${hallTabIdx}`"
      >
        {{ hall.attributes.name }}
      </v-tab>
    </v-tabs>

    <v-tabs-items
      v-model="currentHallIdx"
      style="background: transparent"
      touchless
    >
      <v-tab-item
        v-for="(hall, hallIdx) in halls"
        :key="`hall_${hallIdx}`"
      >
        <form @submit.prevent="handleClickSubmit">
          <v-card class="mb-8">
            <v-card-title>基本情報</v-card-title>
            <v-card-text>
              <v-row>
                <v-col cols="4">
                  <v-text-field
                    v-model.trim="formTemp.name"
                    label="名前"
                    required="true"
                    :error="!isValid.name"
                  />
                </v-col>

                <v-col cols="4">
                  <v-text-field
                    v-model.number="formTemp.tableCount"
                    label="卓数"
                    type="number"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    min="1"
                    max="100"
                    required="true"
                    :error="!isValid.tableCount"
                  />
                </v-col>

                <v-col cols="4">
                  <v-text-field
                    v-model.number="formTemp.position"
                    label="順番"
                    type="number"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    min="1"
                    max="100"
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col
                  md="4"
                  cols="12"
                >
                  <v-text-field
                    :value="`${formTemp.tablePrefix || ''}1${formTemp.tableSuffix || ''}`"
                    label="卓表示名プレビュー"
                    readonly
                    outlined
                  />
                </v-col>

                <v-col
                  md="4"
                  cols="6"
                >
                  <v-text-field
                    v-model.trim="formTemp.tablePrefix"
                    label="卓名 接頭辞"
                  />
                </v-col>

                <v-col
                  md="4"
                  cols="6"
                >
                  <v-text-field
                    v-model.trim="formTemp.tableSuffix"
                    label="卓名 接尾辞"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <template v-if="formTemp.tableInitConfigAttributes">
            <v-card class="mb-8">
              <v-card-title>卓作成設定</v-card-title>
              <v-card-text>
                <yona-has-many-v-select
                  v-model="formTemp.tableInitConfigAttributes.tableInitReferralsAttributes"
                  parent-name="tableInitConfig"
                  :parent-id="formTemp.tableInitConfigAttributes.id"
                  :children="referrals"
                  child-name="referral"
                  :label="currentClub.referralAlias"
                />

                <yona-has-many-v-select
                  v-model="formTemp.tableInitConfigAttributes.tableInitNominationsAttributes"
                  parent-name="tableInitConfig"
                  :parent-id="formTemp.tableInitConfigAttributes.id"
                  :children="nominations"
                  child-name="nomination"
                  :label="currentClub.nominationAlias"
                />

                <yona-has-many-v-select
                  v-model="formTemp.tableInitConfigAttributes.tableInitCoursesAttributes"
                  parent-name="tableInitConfig"
                  :parent-id="formTemp.tableInitConfigAttributes.id"
                  :children="courses"
                  child-name="course"
                  :label="currentClub.courseAlias"
                />

                <yona-has-many-v-select
                  v-model="formTemp.tableInitConfigAttributes.tableInitItemsAttributes"
                  parent-name="tableInitConfig"
                  :parent-id="formTemp.tableInitConfigAttributes.id"
                  :children="items"
                  child-name="item"
                  label="チャージ"
                />

                <v-select
                  v-model="formTemp.tableInitConfigAttributes.autoExtensionCourseId"
                  :items="courseOptions"
                  item-text="name"
                  item-value="id"
                  label="自動延長コース"
                  hint="この席種で自動延長するときに使う延長コース（未選択なら店舗設定に従います）"
                  persistent-hint
                  clearable
                  dense
                />
              </v-card-text>
            </v-card>
          </template>

          <template v-if="formTemp.checkoutConfigAttributes">
            <v-card class="mb-8">
              <v-card-title>会計設定</v-card-title>

              <v-card-text>
                <v-card
                  elevation="0"
                  class="mb-8 pa-4"
                  outlined
                >
                  <v-card-title class="text-base pa-0 mb-2">
                    TAX
                    <v-spacer />
                    <v-btn
                      fab
                      icon
                      :ripple="false"
                      :color="isPreview.tax ? 'primary' : null"
                      @click="isPreview.tax = !isPreview.tax"
                    >
                      <v-icon>
                        {{ icons.mdiCalculatorVariantOutline }}
                      </v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-text class="pa-0">
                    <v-row class="pl-1">
                      <v-col
                        md="2"
                        cols="6"
                      >
                        <v-text-field
                          :value="`${(formTemp.checkoutConfigAttributes.serviceChargeRate * 100).toFixed()}`"
                          label="率"
                          type="number"
                          inputmode="numeric"
                          pattern="[0-9]*"
                          step="1"
                          min="0"
                          suffix="%"
                          @input="formTemp.checkoutConfigAttributes.serviceChargeRate = ($event / 100).toFixed(2)"
                        />
                      </v-col>

                      <v-col
                        md="2"
                        cols="6"
                      >
                        <v-select
                          v-model="formTemp.checkoutConfigAttributes.taxMode"
                          :items="taxModes"
                          item-text="locale"
                          item-value="value"
                          label="消費税"
                          mandatory
                          :menu-props="{ auto: true, offsetY: true }"
                          no-data-text="選択可能なデータがありません"
                        />
                      </v-col>

                      <v-col
                        md="2"
                        cols="12"
                      >
                        <v-select
                          v-model="formTemp.checkoutConfigAttributes.taxRoundingMethod"
                          :items="roundingMethods"
                          item-text="locale"
                          item-value="value"
                          label="丸め処理"
                          mandatory
                          :menu-props="{ auto: true, offsetY: true }"
                          no-data-text="選択可能なデータがありません"
                        />
                      </v-col>

                      <v-col
                        v-if="formTemp.checkoutConfigAttributes.taxRoundingMethod !== 'ceil_to_multiple'"
                        md="6"
                        cols="12"
                      >
                        <v-slider
                          :value="Math.abs(formTemp.checkoutConfigAttributes.taxRoundingDigit)"
                          :tick-labels="['0', '一', '十', '百', '千', '万']"
                          :min="0"
                          :max="5"
                          label="丸める位"
                          step="1"
                          ticks="always"
                          tick-size="4"
                          @input="formTemp.checkoutConfigAttributes.taxRoundingDigit = ($event * -1)"
                        />
                      </v-col>
                      <v-col
                        v-else
                        md="2"
                        cols="12"
                      >
                        <v-text-field
                          v-model.number="formTemp.checkoutConfigAttributes.taxRoundingMultiple"
                          type="number"
                          inputmode="numeric"
                          pattern="[0-9]*"
                          min="0"
                          label="倍数"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>

                  <v-expand-transition>
                    <div v-show="isPreview.tax">
                      <v-divider class="mt-4" />
                      <v-card-subtitle class="px-0">
                        プレビュー
                      </v-card-subtitle>
                      <v-row>
                        <v-col
                          md="6"
                          cols="12"
                        >
                          <v-text-field
                            :value="calcPreviewResult.tax.toLocaleString()"
                            label="算出されたTAX"
                            outlined
                            prefix="¥"
                            readonly
                            :loading="isCalculating.tax"
                          />
                        </v-col>

                        <v-col
                          md="6"
                          cols="12"
                        >
                          <v-text-field
                            v-model.number="calcPreviewInput.tax"
                            label="金額"
                            type="number"
                            inputmode="numeric"
                            prefix="¥"
                            pattern="[0-9]*"
                          >
                            <template #append-outer>
                              <v-btn
                                :ripple="false"
                                small
                                color="primary"
                                :disabled="!isValidToCalcPreview.tax || isCalculating.tax"
                                :loading="isCalculating.tax"
                                @click="calcTax"
                              >
                                計算
                              </v-btn>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>
                    </div>
                  </v-expand-transition>
                </v-card>

                <v-card
                  elevation="0"
                  class="mb-8 pa-4"
                  outlined
                >
                  <v-card-title class="text-base pa-0 mb-2">
                    合計金額

                    <v-spacer />
                    <v-btn
                      :ripple="false"
                      fab
                      icon
                      :color="isPreview.total ? 'primary' : null"
                      @click="isPreview.total = !isPreview.total"
                    >
                      <v-icon>
                        {{ icons.mdiCalculatorVariantOutline }}
                      </v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-text class="pa-0">
                    <v-row>
                      <v-col
                        md="2"
                        cols="12"
                      >
                        <v-select
                          v-model="formTemp.checkoutConfigAttributes.totalRoundingMethod"
                          :items="roundingMethods"
                          item-text="locale"
                          item-value="value"
                          label="丸め処理"
                          mandatory
                          :menu-props="{ auto: true, offsetY: true }"
                          no-data-text="選択可能なデータがありません"
                        />
                      </v-col>

                      <template v-if="formTemp.checkoutConfigAttributes.totalRoundingMethod !== 'ceil_to_multiple'">
                        <v-spacer />
                        <v-col
                          md="6"
                          cols="12"
                        >
                          <v-slider
                            :value="Math.abs(formTemp.checkoutConfigAttributes.totalRoundingDigit)"
                            :tick-labels="['0', '一', '十', '百', '千', '万']"
                            :min="0"
                            :max="5"
                            label="丸める位"
                            step="1"
                            ticks="always"
                            tick-size="4"
                            @input="formTemp.checkoutConfigAttributes.totalRoundingDigit = ($event * -1)"
                          />
                        </v-col>
                      </template>
                      <v-col
                        v-else
                        md="2"
                        cols="12"
                      >
                        <v-text-field
                          v-model.number="formTemp.checkoutConfigAttributes.totalRoundingMultiple"
                          type="number"
                          inputmode="numeric"
                          pattern="[0-9]*"
                          min="0"
                          label="倍数"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>

                  <v-expand-transition>
                    <div v-show="isPreview.total">
                      <v-divider class="mt-4" />
                      <v-card-subtitle class="px-0">
                        プレビュー
                      </v-card-subtitle>
                      <v-row>
                        <v-col
                          md="6"
                          cols="12"
                        >
                          <v-text-field
                            :value="calcPreviewResult.total.toLocaleString()"
                            label="算出された合計金額"
                            outlined
                            prefix="¥"
                            readonly
                            :loading="isCalculating.total"
                          />
                        </v-col>

                        <v-col
                          md="6"
                          cols="12"
                        >
                          <v-text-field
                            v-model.number="calcPreviewInput.total"
                            label="金額"
                            type="number"
                            inputmode="numeric"
                            prefix="¥"
                            pattern="[0-9]*"
                          >
                            <template #append-outer>
                              <v-btn
                                small
                                color="primary"
                                :ripple="false"
                                :disabled="!isValidToCalcPreview.total|| isCalculating.total"
                                :loading="isCalculating.total"
                                @click="calcTotal"
                              >
                                計算
                              </v-btn>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>
                    </div>
                  </v-expand-transition>
                </v-card>

                <v-card
                  elevation="0"
                  class="pa-4"
                  outlined
                >
                  <v-card-title class="text-base pa-0 mb-2">
                    会計手数料

                    <v-spacer />
                    <v-btn
                      fab
                      icon
                      :ripple="false"
                      :color="isPreview.processingFee ? 'primary' : null"
                      @click="isPreview.processingFee = !isPreview.processingFee"
                    >
                      <v-icon>
                        {{ icons.mdiCalculatorVariantOutline }}
                      </v-icon>
                    </v-btn>
                  </v-card-title>
                  <v-card-text class="pa-0">
                    <v-row class="pl-1">
                      <v-col
                        md="2"
                        cols="6"
                      >
                        <v-text-field
                          :value="`${(formTemp.checkoutConfigAttributes.processingFeeRate * 100).toFixed()}`"
                          label="率"
                          type="number"
                          inputmode="numeric"
                          pattern="[0-9]*"
                          step="1"
                          min="0"
                          suffix="%"
                          @input="formTemp.checkoutConfigAttributes.processingFeeRate = ($event / 100).toFixed(2)"
                        />
                      </v-col>

                      <v-col
                        md="2"
                        cols="6"
                      >
                        <v-select
                          v-model="formTemp.checkoutConfigAttributes.processingFeeTaxMode"
                          :items="taxModes"
                          item-text="locale"
                          item-value="value"
                          label="消費税"
                          mandatory
                          :menu-props="{ auto: true, offsetY: true }"
                          no-data-text="選択可能なデータがありません"
                        />
                      </v-col>

                      <v-col
                        md="2"
                        cols="12"
                      >
                        <v-select
                          v-model="formTemp.checkoutConfigAttributes.processingFeeRoundingMethod"
                          :items="roundingMethods"
                          item-text="locale"
                          item-value="value"
                          label="丸め処理"
                          mandatory
                          :menu-props="{ auto: true, offsetY: true }"
                          no-data-text="選択可能なデータがありません"
                        />
                      </v-col>

                      <v-col
                        v-if="formTemp.checkoutConfigAttributes.processingFeeRoundingMethod !== 'ceil_to_multiple'"
                        md="6"
                        cols="12"
                      >
                        <v-slider
                          :value="Math.abs(formTemp.checkoutConfigAttributes.processingFeeRoundingDigit)"
                          :tick-labels="['0', '一', '十', '百', '千', '万']"
                          :min="0"
                          :max="5"
                          label="丸める位"
                          step="1"
                          ticks="always"
                          tick-size="4"
                          @input="formTemp.checkoutConfigAttributes.processingFeeRoundingDigit = ($event * -1)"
                        />
                      </v-col>
                      <v-col
                        v-else
                        md="2"
                        cols="12"
                      >
                        <v-text-field
                          v-model.number="formTemp.checkoutConfigAttributes.processingFeeRoundingMultiple"
                          type="number"
                          inputmode="numeric"
                          pattern="[0-9]*"
                          min="0"
                          label="倍数"
                        />
                      </v-col>
                    </v-row>
                  </v-card-text>
                  <v-expand-transition>
                    <div v-show="isPreview.processingFee">
                      <v-divider class="mt-4" />
                      <v-card-subtitle class="px-0">
                        プレビュー
                      </v-card-subtitle>
                      <v-row>
                        <v-col
                          md="6"
                          cols="12"
                        >
                          <v-text-field
                            :value="calcPreviewResult.processingFee.toLocaleString()"
                            label="算出された会計手数料"
                            outlined
                            prefix="¥"
                            readonly
                            :loading="isCalculating.processingFee"
                          />
                        </v-col>

                        <v-col
                          md="6"
                          cols="12"
                        >
                          <v-text-field
                            v-model.number="calcPreviewInput.processingFee"
                            label="金額"
                            type="number"
                            inputmode="numeric"
                            prefix="¥"
                            pattern="[0-9]*"
                          >
                            <template #append-outer>
                              <v-btn
                                small
                                color="primary"
                                :ripple="false"
                                :disabled="!isValidToCalcPreview.processingFee|| isCalculating.processingFee"
                                :loading="isCalculating.processingFee"
                                @click="calcProcessingFee"
                              >
                                計算
                              </v-btn>
                            </template>
                          </v-text-field>
                        </v-col>
                      </v-row>
                    </div>
                  </v-expand-transition>
                </v-card>
              </v-card-text>
            </v-card>
          </template>

          <v-card class="mb-8">
            <v-card-title>
              伝票フッター設定
            </v-card-title>

            <v-card-text>
              <v-switch
                v-model="slipTemplate.enabled"
                label="フッターを有効にする"
                class="mt-0"
              />

              <div class="mb-4">
                <div class="text-subtitle-2 mb-2">
                  適用伝票タイプ（未選択の場合は全タイプに適用）
                </div>
                <v-row>
                  <v-col
                    v-for="st in slipTypeOptions"
                    :key="st.value"
                    cols="6"
                    md="3"
                  >
                    <v-checkbox
                      v-model="slipTemplate.slipTypes"
                      :label="st.text"
                      :value="st.value"
                      hide-details
                      class="mt-0"
                    />
                  </v-col>
                </v-row>
              </div>

              <v-divider class="mb-4" />

              <div class="text-subtitle-2 mb-2">
                定数設定
              </div>
              <div
                v-for="(constant, constIdx) in slipTemplateConstants"
                :key="`const_${constIdx}`"
                class="d-flex align-center mb-2"
              >
                <v-text-field
                  v-model="constant.key"
                  label="キー"
                  dense
                  hide-details
                  class="mr-2"
                  style="max-width: 200px"
                />
                <v-text-field
                  v-model.number="constant.value"
                  label="値"
                  type="number"
                  dense
                  hide-details
                  class="mr-2"
                  style="max-width: 200px"
                />
                <v-btn
                  icon
                  small
                  color="error"
                  :ripple="false"
                  @click="removeConstant(constIdx)"
                >
                  <v-icon small>
                    {{ icons.mdiTrashCanOutline }}
                  </v-icon>
                </v-btn>
              </div>
              <v-btn
                small
                text
                color="primary"
                :ripple="false"
                @click="addConstant"
              >
                <v-icon
                  small
                  left
                >
                  {{ icons.mdiPlus }}
                </v-icon>
                定数を追加
              </v-btn>

              <v-divider class="my-4" />

              <div class="text-subtitle-2 mb-2">
                行アイテム
              </div>
              <div
                v-for="(item, itemIdx) in slipTemplate.items"
                :key="`item_${itemIdx}`"
                class="d-flex align-center mb-2"
              >
                <v-select
                  v-model="item.type"
                  :items="itemTypeOptions"
                  dense
                  hide-details
                  class="mr-2"
                  style="max-width: 140px"
                />

                <template v-if="item.type === 'text'">
                  <v-text-field
                    v-model="item.content"
                    label="テキスト"
                    dense
                    hide-details
                    class="mr-2"
                  />
                </template>

                <template v-if="item.type === 'expression'">
                  <v-text-field
                    v-model="item.label"
                    label="ラベル"
                    dense
                    hide-details
                    class="mr-2"
                    style="max-width: 200px"
                  />
                  <v-text-field
                    v-model="item.expression"
                    label="式"
                    dense
                    hide-details
                    class="mr-2"
                  />
                  <v-checkbox
                    v-model="item.show_currency"
                    label="¥マーク"
                    dense
                    hide-details
                    class="mt-0 mr-2"
                    style="flex: 0 0 auto"
                  />
                </template>

                <template v-if="item.type === 'divider'">
                  <v-divider class="mr-2" />
                </template>

                <v-btn
                  icon
                  small
                  :ripple="false"
                  :disabled="itemIdx === 0"
                  @click="moveItem(itemIdx, -1)"
                >
                  <v-icon small>
                    {{ icons.mdiArrowUp }}
                  </v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  :ripple="false"
                  :disabled="itemIdx === slipTemplate.items.length - 1"
                  @click="moveItem(itemIdx, 1)"
                >
                  <v-icon small>
                    {{ icons.mdiArrowDown }}
                  </v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  color="error"
                  :ripple="false"
                  @click="removeItem(itemIdx)"
                >
                  <v-icon small>
                    {{ icons.mdiTrashCanOutline }}
                  </v-icon>
                </v-btn>
              </div>

              <div class="d-flex">
                <v-btn
                  small
                  text
                  color="primary"
                  :ripple="false"
                  @click="addItem('text')"
                >
                  <v-icon
                    small
                    left
                  >
                    {{ icons.mdiPlus }}
                  </v-icon>
                  テキスト追加
                </v-btn>
                <v-btn
                  small
                  text
                  color="primary"
                  :ripple="false"
                  @click="addItem('divider')"
                >
                  <v-icon
                    small
                    left
                  >
                    {{ icons.mdiPlus }}
                  </v-icon>
                  区切り線追加
                </v-btn>
                <v-btn
                  small
                  text
                  color="primary"
                  :ripple="false"
                  @click="addItem('expression')"
                >
                  <v-icon
                    small
                    left
                  >
                    {{ icons.mdiPlus }}
                  </v-icon>
                  式追加
                </v-btn>
              </div>

              <v-btn
                color="primary"
                class="mt-4"
                :ripple="false"
                @click="saveSlipTemplate"
              >
                フッター設定を保存
              </v-btn>

              <v-divider class="my-4" />

              <div class="text-subtitle-2 mb-2">
                プレビュー
              </div>
              <v-row>
                <v-col
                  v-for="ctx in slipPreviewContextFields"
                  :key="ctx.key"
                  cols="6"
                  md="3"
                >
                  <v-text-field
                    v-model.number="slipPreviewContext[ctx.key]"
                    :label="ctx.label"
                    type="number"
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-btn
                small
                color="primary"
                class="mt-4"
                :ripple="false"
                :loading="isSlipPreviewLoading"
                @click="previewSlipTemplate"
              >
                プレビュー
              </v-btn>

              <v-card
                v-if="slipPreviewItems.length > 0"
                outlined
                class="mt-4 pa-4"
                elevation="0"
              >
                <div class="text-subtitle-2 mb-2">
                  プレビュー結果
                </div>
                <div
                  v-for="(previewItem, previewIdx) in slipPreviewItems"
                  :key="`preview_${previewIdx}`"
                >
                  <template v-if="previewItem.type === 'text'">
                    <div class="text-center my-1">
                      {{ previewItem.content }}
                    </div>
                  </template>
                  <template v-else-if="previewItem.type === 'divider'">
                    <v-divider class="my-1" />
                  </template>
                  <template v-else-if="previewItem.type === 'expression'">
                    <div class="d-flex justify-space-between my-1">
                      <span>{{ previewItem.label }}</span>
                      <span>{{ previewItem.formattedValue }}</span>
                    </div>
                  </template>
                </div>
              </v-card>
            </v-card-text>
          </v-card>

          <v-row class="d-flex align-center justify-end">
            <v-col
              md="3"
              cols="6"
            >
              <v-btn
                color="primary"
                :ripple="false"
                :disabled="Object.values({ ...isValid, ...isValidToCalcPreview }).includes(false)"
                :loading="isSubmitting"
                type="submit"
                block
              >
                {{ addMode ? '作成' : '更新' }}
              </v-btn>
            </v-col>

            <v-col
              md="3"
              cols="6"
            >
              <v-btn
                v-if="!addMode && halls.length > 1"
                :ripple="false"
                color="error"
                block
                @click="isDeleting = true"
              >
                <v-icon left>
                  {{ icons.mdiTrashCanOutline }}
                </v-icon>削除
              </v-btn>

              <v-btn
                v-if="addMode"
                block
                text
                :ripple="false"
                @click="currentHallIdx = 0"
              >
                キャンセル
              </v-btn>
            </v-col>
          </v-row>
        </form>
      </v-tab-item>
    </v-tabs-items>

    <v-dialog
      v-model="isDeleting"
      width="500"
    >
      <v-card>
        <v-card-title>
          <div>削除の確認</div>
        </v-card-title>
        <v-card-text>
          <p>削除を実行してもよろしいですか</p>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            :ripple="false"
            color="error"
            @click="deleteHall"
          >
            削除
          </v-btn>
          <v-btn
            :ripple="false"
            color="blue darken-1"
            text
            @click="isDeleting = false"
          >
            キャンセル
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  getCurrentInstance,
  ref,
  watch,
  computed,
  reactive,
} from '@vue/composition-api'
import {
  mdiTrashCanOutline,
  mdiPlus,
  mdiCalculatorVariantOutline,
  mdiArrowUp,
  mdiArrowDown,
} from '@mdi/js'
import {
  sortBy,
  groupBy,
  map,
  includes,
} from 'lodash'
import { getVuetify } from '@core/utils'
import HallApi from '@/api/waiter/Hall'
import ReferralApi from '@/api/waiter/Referral'
import CheckoutConfigApi from '@/api/waiter/CheckoutConfig'
import VendibleApi from '@/api/waiter/Vendible'
import SlipTemplateApi from '@/api/v2/utility/settings/SlipTemplate'
import YonaHasManyVSelect from '@/views/components/util/YonaHasManyVSelect.vue'
import { toCourseOptions } from '@/views/composable/useCourseOptions'

export default {
  components: {
    YonaHasManyVSelect,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const $vuetify = getVuetify()

    const top = ref()
    const isLoading = ref(false)
    const isSubmitting = ref(false)
    const isDeleting = ref(false)
    const currentHallIdx = ref()
    const halls = ref([])
    const formTemp = ref({
      name: '',
      tableCount: 12,
      tablePrefix: '',
      tableSuffix: '卓',
      position: halls.value.length,
      tableInitConfigAttributes: {
        autoExtensionCourseId: null,
        tableInitReferralsAttributes: [],
        tableInitNominationsAttributes: [],
        tableInitCoursesAttributes: [],
        tableInitItemsAttributes: [],
      },
      checkoutConfigAttributes: {
        serviceChargeRate: 0,
        taxMode: 'normal',
        taxRoundingMethod: 'round',
        taxRoundingDigit: 0,
        totalRoundingMethod: 'round',
        totalRoundingDigit: 0,
        processingFeeRate: 0,
        processingFeeTaxMode: 'normal',
        processingFeeRoundingMethod: 'round',
        processingFeeRoundingDigit: 0,
        processingFeeRoundingMultiple: 0,
        taxRoundingMultiple: 0,
        totalRoundingMultiple: 0,
      },
    })
    const addMode = ref(false)
    const referrals = ref([])
    const nominations = ref([])
    const courses = ref([])
    const items = ref([])
    // 自動延長コース選択用の選択肢 [{ id, name }]
    const courseOptions = computed(() => toCourseOptions(courses.value))
    const taxModes = ref([])
    const roundingMethods = ref([])

    // --- Slip Template ---
    const slipTemplate = reactive({
      id: null,
      enabled: true,
      slipTypes: [],
      items: [],
      constants: {},
    })
    const slipTemplateConstants = ref([])
    const slipTypeOptions = [
      { text: '会計伝票', value: 'accounting' },
      { text: '精算伝票', value: 'settlement' },
      { text: '概算伝票', value: 'estimated' },
      { text: '領収書', value: 'receipt' },
    ]
    const itemTypeOptions = [
      { text: 'テキスト', value: 'text' },
      { text: '区切り線', value: 'divider' },
      { text: '式', value: 'expression' },
    ]
    const slipPreviewContext = reactive({
      REAL_TOTAL: 100000,
      SUBTOTAL: 80000,
      TOTAL: 100000,
      SERVICE_FEE: 10000,
      PROCESSING_FEE: 10000,
      DISCOUNT_AMOUNT: 0,
      CASH_PAYMENT_AMOUNT: 60000,
      CARD_PAYMENT_AMOUNT: 40000,
      CUSTOMER_COUNT: 3,
      指名人数: 2,
    })
    const slipPreviewContextFields = [
      { key: 'REAL_TOTAL', label: '実合計' },
      { key: 'SUBTOTAL', label: '小計' },
      { key: 'TOTAL', label: '合計' },
      { key: 'SERVICE_FEE', label: 'サービス料' },
      { key: 'PROCESSING_FEE', label: '税サ' },
      { key: 'DISCOUNT_AMOUNT', label: '値引き額' },
      { key: 'CASH_PAYMENT_AMOUNT', label: '現金支払額' },
      { key: 'CARD_PAYMENT_AMOUNT', label: 'カード支払額' },
      { key: 'CUSTOMER_COUNT', label: '来客数' },
      { key: '指名人数', label: '指名人数' },
    ]
    const slipPreviewItems = ref([])
    const isSlipPreviewLoading = ref(false)

    const constantsToArray = obj => {
      return Object.entries(obj || {}).map(([key, value]) => ({ key, value }))
    }

    const arrayToConstants = arr => {
      const result = {}
      arr.forEach(item => {
        if (item.key) {
          result[item.key] = item.value
        }
      })

      return result
    }

    const loadSlipTemplate = async hallId => {
      if (!hallId) return

      try {
        const res = await SlipTemplateApi.getSlipTemplates({ hall_id: hallId, position: 'footer' })
        const records = res?.data?.data?.data || res?.data?.data || []
        if (records.length > 0) {
          const tmpl = records[0]
          const attrs = tmpl.attributes || tmpl
          slipTemplate.id = tmpl.id || attrs.id
          slipTemplate.enabled = attrs.enabled !== undefined ? attrs.enabled : true
          slipTemplate.slipTypes = attrs.slip_types || attrs.slipTypes || []
          slipTemplate.items = (attrs.items || []).map(item => {
            const copy = { ...item }
            // 既存データ（show_currency未設定）の式は従来通り¥表示をデフォルトにする
            if (copy.type === 'expression' && copy.show_currency === undefined) {
              copy.show_currency = true
            }

            return copy
          })
          slipTemplate.constants = attrs.constants || {}
          slipTemplateConstants.value = constantsToArray(slipTemplate.constants)
        } else {
          slipTemplate.id = null
          slipTemplate.enabled = true
          slipTemplate.slipTypes = []
          slipTemplate.items = []
          slipTemplate.constants = {}
          slipTemplateConstants.value = []
        }
      } catch {
        slipTemplate.id = null
        slipTemplate.enabled = true
        slipTemplate.slipTypes = []
        slipTemplate.items = []
        slipTemplate.constants = {}
        slipTemplateConstants.value = []
      }

      slipPreviewItems.value = []
    }

    const saveSlipTemplate = async () => {
      const currentHall = halls.value[currentHallIdx.value]
      if (!currentHall || !currentHall.id) return

      slipTemplate.constants = arrayToConstants(slipTemplateConstants.value)

      const payload = {
        slip_template: {
          hall_id: currentHall.id,
          position: 'footer',
          name: `${currentHall.attributes.name} フッター`,
          enabled: slipTemplate.enabled,
          slip_types: slipTemplate.slipTypes,
          items: slipTemplate.items,
          constants: slipTemplate.constants,
        },
      }

      try {
        if (slipTemplate.id) {
          const res = await SlipTemplateApi.updateSlipTemplate(slipTemplate.id, payload)
          const tmpl = res?.data?.data?.data || res?.data?.data
          if (tmpl) {
            const attrs = tmpl.attributes || tmpl
            slipTemplate.id = tmpl.id || attrs.id
          }
        } else {
          const res = await SlipTemplateApi.createSlipTemplate(payload)
          const tmpl = res?.data?.data?.data || res?.data?.data
          if (tmpl) {
            const attrs = tmpl.attributes || tmpl
            slipTemplate.id = tmpl.id || attrs.id
          }
        }
        vm.$toast.success('伝票フッター設定を保存しました')
      } catch {
        vm.$toast.error('伝票テンプレートの保存に失敗しました')
      }
    }

    const addConstant = () => {
      slipTemplateConstants.value.push({ key: '', value: 0 })
    }

    const removeConstant = idx => {
      slipTemplateConstants.value.splice(idx, 1)
    }

    const addItem = type => {
      const newItem = { type }
      if (type === 'text') {
        newItem.content = ''
      } else if (type === 'expression') {
        newItem.label = ''
        newItem.expression = ''
        newItem.show_currency = true
      }
      slipTemplate.items.push(newItem)
    }

    const removeItem = idx => {
      slipTemplate.items.splice(idx, 1)
    }

    const moveItem = (idx, direction) => {
      const newIdx = idx + direction
      if (newIdx < 0 || newIdx >= slipTemplate.items.length) return
      const temp = slipTemplate.items[idx]
      slipTemplate.items.splice(idx, 1)
      slipTemplate.items.splice(newIdx, 0, temp)
    }

    const previewSlipTemplate = async () => {
      const currentHall = halls.value[currentHallIdx.value]
      if (!currentHall) return

      isSlipPreviewLoading.value = true

      try {
        const res = await SlipTemplateApi.evaluateSlipTemplate({
          hall_id: currentHall.id,
          slip_type: 'accounting',
          position: 'footer',
          preview: true,
          context: { ...slipPreviewContext },
        })

        if (res?.data?.data?.items) {
          slipPreviewItems.value = res.data.data.items
        } else if (res?.data?.items) {
          slipPreviewItems.value = res.data.items
        }
      } catch {
        vm.$toast.error('プレビューに失敗しました')
      }

      isSlipPreviewLoading.value = false
    }

    const isPreview = reactive({
      tax: false,
      total: false,
      processingFee: false,
    })
    const calcPreviewInput = reactive({
      tax: 0,
      total: 0,
      processingFee: 0,
    })
    const calcPreviewResult = reactive({
      tax: 0,
      total: 0,
      processingFee: 0,
    })
    const isCalculating = reactive({
      tax: false,
      total: false,
      processingFee: false,
    })

    const isValid = computed(() => {
      const { name, tableCount } = formTemp.value

      return {
        name: !!name,
        tableCount: !!tableCount && (tableCount > 0 && tableCount <= 100),
      }
    })

    const isValidToCalcPreview = computed(() => {
      const isValidTaxMode = str => includes(map(taxModes.value, 'value'), str)
      const isValidRoundingMethod = str => includes(map(roundingMethods.value, 'value'), str)

      const {
        taxMode,
        taxRoundingMethod,
        totalRoundingMethod,
        processingFeeTaxMode,
        processingFeeRoundingMethod,
      } = formTemp.value.checkoutConfigAttributes

      return {
        tax: isValidTaxMode(taxMode) && isValidRoundingMethod(taxRoundingMethod),
        total: isValidRoundingMethod(totalRoundingMethod),
        processingFee: isValidTaxMode(processingFeeTaxMode) && isValidRoundingMethod(processingFeeRoundingMethod),
      }
    })

    const getHalls = async () => {
      isLoading.value = true
      const res = await HallApi.getHalls({ isOperation: true, isSettings: true })

      if (res?.data) {
        halls.value = [...sortBy(res.data.halls.data, 'attributes.position')]
      }

      isLoading.value = false
    }

    const getReferrals = async () => {
      const res = await ReferralApi.getReferrals()

      if (res?.data) {
        referrals.value = [...res.data.referrals.data]
      }
    }

    const getVendibles = async () => {
      const res = await VendibleApi.getVendibles()

      if (res?.data) {
        const vendibles = [...res.data.vendibles.data]

        const vendiblesByType = groupBy(vendibles, 'attributes.vendibleType')
        courses.value = vendiblesByType.Course
        nominations.value = vendiblesByType.Nomination
        items.value = vendiblesByType.Item
      }
    }

    const getCheckoutConfigEnums = async () => {
      const res = await CheckoutConfigApi.getEnums()

      if (res?.data) {
        const buildVSelectItems = enums => {
          return map(enums, (value, key) => {
            return {
              value: key,
              locale: value,
            }
          })
        }

        taxModes.value = buildVSelectItems({ ...res.data.taxModes })
        roundingMethods.value = buildVSelectItems({ ...res.data.roundingMethods })
      }
    }

    const deleteHall = async () => {
      isDeleting.value = false

      if (halls.value.length <= 1) {
        vm.$toast.error('ホールを削除に失敗しました')

        return
      }

      const res = await HallApi.deleteHall({
        hallId: halls.value[currentHallIdx.value].id,
      })

      if (res.data.status === 'error') {
        vm.$toast.error('削除できません')
        vm.$toast.error(res.data.message.join('\n'))

        return
      }

      if (res?.status === 200) {
        halls.value.splice(currentHallIdx.value, 1)
        $vuetify.goTo(top.value)
        vm.$toast.success('削除しました')
      }
    }

    const calcTax = async () => {
      isCalculating.tax = true

      const {
        serviceChargeRate,
        taxMode,
        taxRoundingMethod,
        taxRoundingDigit,
        taxRoundingMultiple,
      } = formTemp.value.checkoutConfigAttributes

      const res = await CheckoutConfigApi.calcTax({
        price: calcPreviewInput.tax,
        serviceChargeRate,
        taxMode,
        taxRoundingMethod,
        taxRoundingDigit,
        taxRoundingMultiple,
      })

      if (res?.data) calcPreviewResult.tax = res.data
      isCalculating.tax = false
    }

    const calcTotal = async () => {
      isCalculating.total = true

      const {
        totalRoundingMethod,
        totalRoundingDigit,
        totalRoundingMultiple,
      } = formTemp.value.checkoutConfigAttributes

      const res = await CheckoutConfigApi.calcTotal({
        price: calcPreviewInput.total,
        totalRoundingMethod,
        totalRoundingDigit,
        totalRoundingMultiple,
      })

      if (res?.data) calcPreviewResult.total = res.data
      isCalculating.total = false
    }

    const calcProcessingFee = async () => {
      isCalculating.processingFee = true

      const {
        processingFeeRate,
        processingFeeTaxMode,
        processingFeeRoundingMethod,
        processingFeeRoundingDigit,
        processingFeeRoundingMultiple,
      } = formTemp.value.checkoutConfigAttributes

      const res = await CheckoutConfigApi.calcProcessingFee({
        price: calcPreviewInput.processingFee,
        processingFeeRate,
        processingFeeTaxMode,
        processingFeeRoundingMethod,
        processingFeeRoundingDigit,
        processingFeeRoundingMultiple,
      })

      if (res?.data) calcPreviewResult.processingFee = res.data
      isCalculating.processingFee = false
    }

    const addHallMock = () => {
      addMode.value = true
      halls.value.push({
        attributes: {
          name: 'NEW',
          tableCount: 12,
          tablePrefix: '',
          tableSuffix: '卓',
          tableLayouts: [],
          checkoutConfig: {
            data: {
              id: '',
              type: 'checkoutConfig',
              attributes: {
                serviceChargeRate: 0,
                taxMode: 'normal',
                taxRoundingMethod: 'round',
                taxRoundingDigit: 0,
                totalRoundingMethod: 'round',
                totalRoundingDigit: 0,
                processingFeeRate: 0,
                processingFeeTaxMode: 'normal',
                processingFeeRoundingMethod: 'round',
                processingFeeRoundingDigit: 0,

              },
            },
          },
          tableInitConfig: {
            data: {
              id: '',
              type: 'tableInitConfig',
              attributes: {
                tableInitReferrals: {
                  data: [],
                },
                tableInitNominations: {
                  data: [],
                },
                tableInitCourses: {
                  data: [],
                },
                tableInitItems: {
                  data: [],
                },
              },
            },
          },
        },
      })

      currentHallIdx.value = halls.value.length - 1
    }

    const buildFormTemp = hall => {
      const {
        name,
        tableCount,
        tablePrefix,
        tableSuffix,
        position,
      } = hall.attributes

      const checkoutConfig = hall.attributes.checkoutConfig.data
      const tableInitConfig = hall.attributes.tableInitConfig.data

      return {
        name,
        tableCount,
        tablePrefix,
        tableSuffix,
        position,
        tableLayouts: hall.attributes.tableLayouts || [],
        checkoutConfigAttributes: {
          id: checkoutConfig.id,
          ...checkoutConfig.attributes,
        },
        tableInitConfigAttributes: {
          id: tableInitConfig.id,
          autoExtensionCourseId: tableInitConfig.attributes.autoExtensionCourseId,
          tableInitReferralsAttributes: map(tableInitConfig.attributes.tableInitReferrals.data, o => {
            return {
              id: o.id,
              ...o.attributes,
              _destroy: false,
            }
          }),
          tableInitNominationsAttributes: map(tableInitConfig.attributes.tableInitNominations.data, o => {
            return {
              id: o.id,
              ...o.attributes,
              _destroy: false,
            }
          }),
          tableInitCoursesAttributes: map(tableInitConfig.attributes.tableInitCourses.data, o => {
            return {
              id: o.id,
              ...o.attributes,
              _destroy: false,
            }
          }),
          tableInitItemsAttributes: map(tableInitConfig.attributes.tableInitItems.data, o => {
            return {
              id: o.id,
              ...o.attributes,
              _destroy: false,
            }
          }),
        },
      }
    }

    const handleClickSubmit = async () => {
      isSubmitting.value = true

      // tableCountを超えるtable_numberのレイアウトはサーバ側バリデーションで弾かれるため除外する
      const filteredTableLayouts = (formTemp.value.tableLayouts || [])
        .filter(layout => Number(layout.table_number) <= Number(formTemp.value.tableCount))

      // table_layoutsはJSONBなのでJSON文字列として送る
      const params = {
        ...formTemp.value,
        tableLayouts: JSON.stringify(filteredTableLayouts),
      }

      if (addMode.value) {
        // NOTE: create hall
        const res = await HallApi.createHall({
          params,
        })

        if (res.data) {
          const hall = res.data.hall.data
          halls.value.splice(currentHallIdx.value, 1, hall)
          formTemp.value = buildFormTemp(hall)
          addMode.value = false
          vm.$toast.success('ホールを作成しました')
        }
      } else {
        // NOTE: update hall
        const res = await HallApi.updateHall({
          hallId: halls.value[currentHallIdx.value].id,
          params,
        })

        if (res.data) {
          const hall = res.data.hall.data
          halls.value.splice(currentHallIdx.value, 1, hall)
          formTemp.value = buildFormTemp(hall)
          vm.$toast.success('ホールを更新しました')
        }
      }

      isSubmitting.value = false
      $vuetify.goTo(top.value)
    }

    watch(currentHallIdx, (newIdx, _prevIdx) => {
      if (addMode.value && newIdx !== halls.value.length - 1) {
        halls.value.pop()
        addMode.value = false
      }

      formTemp.value = buildFormTemp(halls.value[currentHallIdx.value])

      isPreview.tax = false
      isPreview.total = false
      isPreview.processingFee = false
      calcPreviewInput.tax = 0
      calcPreviewInput.total = 0
      calcPreviewInput.processingFee = 0
      calcPreviewResult.tax = 0
      calcPreviewResult.total = 0
      calcPreviewResult.processingFee = 0

      // Load slip template for the selected hall
      const currentHall = halls.value[currentHallIdx.value]
      if (currentHall && currentHall.id) {
        loadSlipTemplate(currentHall.id)
      }
    })

    getHalls()
    getReferrals()
    getVendibles()
    getCheckoutConfigEnums()

    return {
      // data
      top,
      isLoading,
      isSubmitting,
      currentHallIdx,
      halls,
      addMode,
      formTemp,
      isDeleting,
      referrals,
      nominations,
      courses,
      courseOptions,
      items,
      taxModes,
      roundingMethods,
      isPreview,
      calcPreviewInput,
      calcPreviewResult,
      isCalculating,

      // computed
      isValid,
      isValidToCalcPreview,

      // slip template
      slipTemplate,
      slipTemplateConstants,
      slipTypeOptions,
      itemTypeOptions,
      slipPreviewContext,
      slipPreviewContextFields,
      slipPreviewItems,
      isSlipPreviewLoading,

      // methods
      handleClickSubmit,
      addHallMock,
      deleteHall,
      calcTax,
      calcTotal,
      calcProcessingFee,
      saveSlipTemplate,
      addConstant,
      removeConstant,
      addItem,
      removeItem,
      moveItem,
      previewSlipTemplate,

      icons: {
        mdiPlus,
        mdiTrashCanOutline,
        mdiCalculatorVariantOutline,
        mdiArrowUp,
        mdiArrowDown,
      },
    }
  },
}
</script>
